var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import { resolveApiUrl } from '@utils/api-client';
import { getStoredAiToken } from './aiTokenStorage';
// ---------------------------------------------------------------------------
// JWT Parsing
// ---------------------------------------------------------------------------
/**
 * Decode JWT payload without verification.
 * Frontend already trusts the token; we just need to read claims.
 */
function decodeJwtPayload(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3)
            return null;
        // Base64url decode the payload
        let payload = parts[1];
        // Convert Base64url to Base64
        payload = payload.replace(/-/g, '+').replace(/_/g, '/');
        // Restore padding - Base64 requires length to be multiple of 4
        while (payload.length % 4 !== 0) {
            payload += '=';
        }
        const decoded = atob(payload);
        return JSON.parse(decoded);
    }
    catch (_a) {
        return null;
    }
}
/**
 * Extract territory claims from the stored AI JWT.
 * Returns unrestricted access if no token or claims are missing (backward compatibility).
 */
export function getTerritoryInfoFromToken() {
    const token = getStoredAiToken();
    if (!token) {
        // No token = treat as unrestricted (backward compatibility)
        return { hasTerritoriesLimitations: false, permittedTerritoryIds: [] };
    }
    const payload = decodeJwtPayload(token);
    if (!payload) {
        // Fail-secure: if we can't decode the token, assume restrictions apply
        return { hasTerritoriesLimitations: true, permittedTerritoryIds: [] };
    }
    return {
        hasTerritoriesLimitations: payload.has_territories_limitations === true,
        permittedTerritoryIds: Array.isArray(payload.permitted_territory_ids)
            ? payload.permitted_territory_ids.filter((id) => typeof id === 'number')
            : []
    };
}
// ---------------------------------------------------------------------------
// Building Territory Lookup
// ---------------------------------------------------------------------------
function numericIds(value) {
    if (!Array.isArray(value))
        return null;
    return value.filter((id) => typeof id === 'number');
}
/**
 * Territories that admit this building: primary plus every secondary.
 *
 * Returns null when the lookup itself failed (the building might or might not
 * be allowed — the caller suppresses). Returns [] when the building is in no
 * territory, which a restricted user cannot see.
 *
 * From the back office this hits `/api/internal` with the Devise cookie. From
 * the technician portal `resolveApiUrl` sends it to `/api/technician_portal`
 * on the tenant Rails host; a relative fetch would hit the portal origin and
 * fail, and failing closed there is what blanked every building-anchored
 * iOS screen for a restricted dual-role tech.
 */
export function fetchBuildingTerritoryIds(buildingId) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const { url, crossOrigin } = resolveApiUrl(`/api/internal/buildings/${buildingId}/territory`);
            const response = yield fetch(url, {
                credentials: crossOrigin ? 'omit' : 'include',
                headers: {
                    Accept: 'application/json'
                }
            });
            if (!response.ok) {
                console.debug('[pageContextValidator] Failed to fetch building territory:', response.status);
                return null;
            }
            const data = (yield response.json());
            const listed = numericIds(data.territory_ids);
            if (listed)
                return listed;
            if (typeof data.territory_id === 'number')
                return [data.territory_id];
            // Older payload: an explicit null primary and no list means "no territory".
            if (data.territory_id === null || data.territory_id === undefined)
                return [];
            return null;
        }
        catch (error) {
            console.debug('[pageContextValidator] Error fetching building territory:', error);
            return null;
        }
    });
}
/**
 * Primary territory id, or null when the building has none or the lookup failed.
 * Prefer `fetchBuildingTerritoryIds` — a restricted user may be admitted by a
 * secondary territory alone.
 */
export function fetchBuildingTerritory(buildingId) {
    return __awaiter(this, void 0, void 0, function* () {
        const ids = yield fetchBuildingTerritoryIds(buildingId);
        if (!ids || ids.length === 0)
            return null;
        return ids[0];
    });
}
// ---------------------------------------------------------------------------
// Page Context Validation
// ---------------------------------------------------------------------------
/**
 * The write-authority fields iOS sends for the orchestrator's write gate. They
 * describe the device, not a building, and can only make the gate refuse a
 * write, so territory has no bearing on them. Dropping them with the rest of a
 * suppressed context switched the gate off for every restricted user on a
 * screen without a permitted building. (`open_visit_id` is part of the iOS
 * contract but not yet read by the gate.)
 */
export const AUTHORITY_KEYS = [
    'open_inspection_id',
    'open_visit_id',
    'inspection_submitted',
    'native_pending_sync'
];
/** The declared keys of a native_pending_sync entry; anything else is dropped on suppression. */
const PENDING_SYNC_ENTRY_KEYS = ['entity_class', 'entity_type', 'local_id', 'server_id', 'operation'];
// Contexts produced by `suppress`. Lets the widget tell a suppressed context
// from a real one without a marker key that would go over the wire.
const suppressedContexts = new WeakSet();
/** True when `context` is the authority-only remnant of a territory-suppressed context. */
export function isTerritorySuppressed(context) {
    return context !== undefined && suppressedContexts.has(context);
}
function pickDefined(source, keys) {
    return Object.fromEntries(keys.filter((key) => source[key] !== undefined).map((key) => [key, source[key]]));
}
/**
 * What a restricted user's context becomes when territory validation rejects
 * it: undefined, unless it is a v2 context carrying write-authority fields, in
 * which case only those survive, under `page_type: 'unknown'`. The page type
 * and route are dropped too: without IDs they would tell the model which kind
 * of record the user is on and invite it to guess which one. Pending-sync
 * entries are rebuilt from their declared keys, because the schema is loose and
 * the coach endpoint forwards page_context to the model as raw JSON.
 */
function suppress(context) {
    if (context.schema_version !== 2)
        return undefined;
    if (!AUTHORITY_KEYS.some((key) => context[key] !== undefined))
        return undefined;
    const authority = pickDefined(context, AUTHORITY_KEYS);
    if (Array.isArray(authority.native_pending_sync)) {
        authority.native_pending_sync = authority.native_pending_sync.map((entry) => pickDefined(entry, PENDING_SYNC_ENTRY_KEYS));
    }
    const suppressed = Object.assign(Object.assign({ schema_version: 2, page_type: 'unknown' }, pickDefined(context, ['surface'])), authority);
    suppressedContexts.add(suppressed);
    return suppressed;
}
/**
 * Validate page context against user's territory restrictions.
 *
 * For unrestricted users: returns context unchanged.
 * For restricted users: a named building is kept only when one of its
 * territories (primary or secondary) is in the permitted list. That is the
 * check — it stops the widget telling the assistant about a building the
 * caller is not granted. iOS screens that do not name a building are the
 * screen the technician already has open, so they pass through; there is no
 * building to withhold. A failed lookup still suppresses, because a named
 * building we could not check is not evidence it is allowed.
 * Suppression returns undefined, or for v2 only the write-authority fields
 * (see `suppress`). Those four fields are never dropped: they only make the
 * write gate stricter.
 *
 * @param context - Page context to validate (may include territoryId from server)
 * @returns Validated context or undefined if suppressed
 */
export function validatePageContext(context) {
    return __awaiter(this, void 0, void 0, function* () {
        // No context = nothing to validate
        if (!context) {
            return undefined;
        }
        const territoryInfo = getTerritoryInfoFromToken();
        // Unrestricted users get context as-is
        if (!territoryInfo.hasTerritoriesLimitations) {
            return context;
        }
        // Dashboard-tier and listing-tier pages have no building scope — pass through
        const NO_BUILDING_PAGE_TYPES = new Set([
            'dashboard',
            'calendar',
            'map',
            'advanced_analytics',
            'buildings_index',
            'inspections_index',
            'work_orders_index',
            'proposals_index',
            'accounts_index',
            'reports_index',
            'inspection_series_index',
            'ai_to_review_inspections',
            // Phase B new listing pages
            'deficiencies_index',
            'contacts_index',
            'invoices_index',
            'account',
            'unknown'
        ]);
        // Resolve the canonical page type and building/territory IDs regardless of v1/v2 shape.
        // v2 payloads carry snake_case fields (page_type, building_id, territory_id);
        // v1 payloads carry camelCase (pageType, buildingId, territoryId).
        const isV2 = context.schema_version === 2;
        const resolvedPageType = isV2 ? context.page_type : context.pageType;
        const resolvedBuildingId = isV2 ? context.building_id : context.buildingId;
        const resolvedTerritoryId = isV2 ? context.territory_id : context.territoryId;
        const hasResolvedBuildingId = resolvedBuildingId !== null && resolvedBuildingId !== undefined;
        // Non-building-scoped page types pass through — unless building_id is also present.
        // A crafted payload could carry building_id alongside a listing/dashboard page_type to
        // smuggle building context past territory validation. If building_id is present we must
        // still validate its territory even on non-building-scoped pages.
        if ((!resolvedPageType || NO_BUILDING_PAGE_TYPES.has(resolvedPageType)) && !hasResolvedBuildingId) {
            return context;
        }
        // No building ID. contact and invoice can exist without a building, on
        // web and iOS. A web page that is building-scoped but didn't say which
        // building is still suppressed — we would be forwarding an entity id we
        // cannot check. An iOS screen is different: the native app built this
        // payload from the screen the technician already has open, and with no
        // building id it does not name a building outside their grant.
        if (resolvedBuildingId === null || resolvedBuildingId === undefined) {
            if (resolvedPageType === 'contact' || resolvedPageType === 'invoice') {
                return context;
            }
            if (isV2 && context.surface === 'ios_webview') {
                return context;
            }
            console.debug('[pageContextValidator] No buildingId, suppressing context');
            return suppress(context);
        }
        // A server-rendered territory that is already in the grant needs no lookup.
        // One that is not might still be admitted by a secondary territory, so look
        // the full set up before deciding the building is outside the grant.
        const permitted = territoryInfo.permittedTerritoryIds;
        let territoryIds;
        if (typeof resolvedTerritoryId === 'number' && permitted.includes(resolvedTerritoryId)) {
            territoryIds = [resolvedTerritoryId];
        }
        else {
            territoryIds = yield fetchBuildingTerritoryIds(resolvedBuildingId);
        }
        // Failed to determine territory = suppress. A lookup error is not the same
        // as "this building is allowed".
        if (territoryIds === null) {
            console.debug('[pageContextValidator] Could not determine building territory, suppressing context');
            return suppress(context);
        }
        const admitted = territoryIds.find((id) => permitted.includes(id));
        if (admitted === undefined) {
            console.debug('[pageContextValidator] Building not in permitted territories, suppressing context');
            return suppress(context);
        }
        // Passed validation - return context with a territory id the caller is
        // actually granted, so the hint does not name a territory they are not in.
        if (isV2) {
            return Object.assign(Object.assign({}, context), { territory_id: admitted });
        }
        return Object.assign(Object.assign({}, context), { territoryId: admitted });
    });
}
